import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';

const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

interface CaptchaWidgetProps {
  siteKey: string;
  onVerify: (token: string) => void;
  /** Only called for real widget / network failures — not when site key is missing */
  onError?: () => void;
}

function CaptchaWidget({ siteKey, onVerify, onError }: CaptchaWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const widgetIdRef = useRef<string | null>(null);
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  onVerifyRef.current = onVerify;
  onErrorRef.current = onError;

  const trimmedKey = (siteKey || '').trim();

  useEffect(() => {
    if (!trimmedKey) {
      setLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;

    const notifyError = () => {
      if (cancelled) return;
      setError(true);
      setLoading(false);
      onErrorRef.current?.();
    };

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      try {
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            /* ignore */
          }
          widgetIdRef.current = null;
        }
        const widgetId = window.turnstile.render(containerRef.current, {
          sitekey: trimmedKey,
          callback: (token: string) => {
            if (cancelled) return;
            setLoading(false);
            setError(false);
            onVerifyRef.current(token);
          },
          'error-callback': (code: string | number) => {
            console.error('[Turnstile] widget error code:', code);
            notifyError();
          },
          theme: 'light',
          size: 'normal',
        });
        widgetIdRef.current = widgetId;
        setLoading(false);
      } catch (err) {
        console.error('Turnstile render error:', err);
        notifyError();
      }
    };

    /** Turnstile needs the container ref committed; retry briefly after script load. */
    const tryRender = (attempt = 0) => {
      if (cancelled) return;
      if (window.turnstile && containerRef.current) {
        renderWidget();
        return;
      }
      if (attempt >= 80) {
        notifyError();
        return;
      }
      window.setTimeout(() => tryRender(attempt + 1), 25);
    };

    const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`) as HTMLScriptElement | null;

    if (existingScript && window.turnstile) {
      tryRender();
    } else if (existingScript) {
      existingScript.addEventListener('load', () => tryRender(), { once: true });
      setLoading(true);
    } else {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (!cancelled) tryRender();
      };
      script.onerror = () => {
        notifyError();
      };
      document.body.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
      }
      widgetIdRef.current = null;
    };
  }, [trimmedKey]);

  if (!trimmedKey) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        <Typography variant="body2">
          Security verification is not configured for this site (missing Turnstile site key). Add{' '}
          <code>REACT_APP_TURNSTILE_SITE_KEY</code> to the frontend build. Submitting an order may fail until this is
          set.
        </Typography>
      </Alert>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
        CAPTCHA could not be loaded. Please refresh the page or try again in a moment.
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 65, gap: 2 }}>
      {loading && <CircularProgress size={24} />}
      <div ref={containerRef} style={{ minHeight: 65, minWidth: 300 }} />
    </Box>
  );
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown> & { 'error-callback'?: (code: string | number) => void }) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

export default CaptchaWidget;
